/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { Constants } from '../../../../../common/Constants';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { stringFormat } from '../../../../../utilities/Utils';
import { ErrorPage } from '../../../../containers/ErrorPage';
import { LoadingPage } from '../../../../containers/LoadingPage';
import { getBodyStyles, getPageStyles, getTitleStyles } from '../../PreSetupTemplateWizard.styles';
import { getFooterStyles, getWarningStyles } from './CreateAdminGroup.styles';

export enum CreateAdminGroupActions {
    create,
    recreate,
    done,
}

export interface CreateAdminGroupComponentProps {
    isLoading?: boolean;
    action?: CreateAdminGroupActions;
    onNext?: (currentAction: CreateAdminGroupActions) => void;
    onBack?: () => void;
    showError?: boolean;
    groupName?: string;
    title?: string;
    description?: string;
}

export const CreateAdminGroupComponent: React.FC<CreateAdminGroupComponentProps> = (
    props: CreateAdminGroupComponentProps
): JSX.Element => {
    const loc = getAppHost().getLocale();
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const { onBack, onNext, action = CreateAdminGroupActions.create, isLoading, showError, groupName } = props;
    let title: string;
    let titleAriaLabel = title;
    let description: JSX.Element;
    let primaryButtonText = loc.strings.done;
    let backButton;
    const loadingString = loc.strings.creating_the_approvals_admin_group;
    let learnMoreLink;
    let description_text1 = loc.strings.create_approvals_admin_group_description_1;
    let description_text2 = loc.strings.create_approvals_admin_group_description_1;

    if (action === CreateAdminGroupActions.recreate) {
        description_text1 = loc.strings.create_approvals_admin_group_description_3;
        description_text2 = loc.strings.create_approvals_admin_group_description_4;
    }

    if (action === CreateAdminGroupActions.create || action === CreateAdminGroupActions.recreate) {
        title = titleAriaLabel = loc.strings.create_approvals_admin_group_title;

        description = (
            <span aria-label={`${description_text1} ${Constants.ADMIN_GROUP_NAME}. ${description_text1}`}>
                <span>{description_text1} "</span>
                <span style={{ fontWeight: 700 }}>{Constants.ADMIN_GROUP_NAME}</span>".
                <span> {description_text2}</span>
            </span>
        );

        backButton = (
            <ActionButton
                ariaLabel={loc.strings.carousel_back_aria_label}
                iconProps={{ iconName: 'ChevronLeft' }}
                text={loc.strings.carousel_back}
                onClick={onBack}
            />
        );

        primaryButtonText = loc.strings.create_action_text;
    } else {
        title = titleAriaLabel = stringFormat(loc.strings.admin_group_is_created_text, { groupName });
        primaryButtonText = loc.strings.done;
        description = (
            <>
                <Stack aria-label={loc.strings.create_approvals_admin_group_successfully_description}>
                    {loc.strings.create_approvals_admin_group_successfully_description}
                </Stack>
                <Stack styles={getWarningStyles(theme)} aria-label={loc.strings.delete_group_warning}>
                    {loc.strings.delete_group_warning}
                </Stack>
            </>
        );
        learnMoreLink = (
            <Link href={Constants.APPROVALS_PERMISSIONS_DOC} target='_blank' aria-label={loc.strings.learn_more_text}>
                {loc.strings.learn_more_text}
            </Link>
        );
    }

    return (
        <Stack styles={getPageStyles}>
            <Stack.Item>
                <Label role='alert' aria-label={titleAriaLabel} styles={getTitleStyles(theme)}>
                    {title}
                </Label>
            </Stack.Item>
            {isLoading ? (
                <Stack.Item styles={getBodyStyles} grow>
                    <LoadingPage message={loadingString} />
                </Stack.Item>
            ) : showError ? (
                <Stack.Item styles={getBodyStyles} grow>
                    <ErrorPage />
                </Stack.Item>
            ) : (
                <>
                    <Stack.Item styles={getBodyStyles} grow>
                        {description}
                    </Stack.Item>
                    <Stack.Item>
                        <Stack
                            styles={getFooterStyles}
                            horizontal
                            verticalAlign='center'
                            horizontalAlign='space-between'>
                            <PrimaryButton
                                disabled={isLoading}
                                text={primaryButtonText}
                                ariaLabel={primaryButtonText}
                                onClick={(): void => {
                                    if (onNext) {
                                        onNext(action);
                                    }
                                }}
                            />
                            {learnMoreLink}
                            {backButton}
                        </Stack>
                    </Stack.Item>
                </>
            )}
        </Stack>
    );
};
